import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
    Table, Input, Button, Row, Col, Card, CardBody, ButtonGroup,
    Collapse, Badge, Tooltip, UncontrolledTooltip, Spinner
} from 'reactstrap';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { FileExcelOutlined, FilterOutlined, ClearOutlined, SettingOutlined } from "@ant-design/icons";
import { AutoSizer } from 'react-virtualized';
import Select from 'react-select';
import { commonKeys, uniqueKeys } from "./datatable-utils";

// Utility function to detect if a string is likely a date
const isDateString = (value) => {
    if (!value || typeof value !== 'string') return false;

    // Common date patterns to check
    const datePatterns = [
        // ISO format: 2025-03-20T00:00:00.000Z or 2025-03-20 00:00:00
        /^\d{4}-\d{2}-\d{2}(T|\s)\d{2}:\d{2}:\d{2}(\.\d{3}Z)?$/,
        // Simple date: 2025-03-20
        /^\d{4}-\d{2}-\d{2}$/,
        // US format: 03/20/2025
        /^\d{2}\/\d{2}\/\d{4}$/,
        // European format: 20.03.2025
        /^\d{2}\.\d{2}\.\d{4}$/,
        // Time formats
        /^\d{2}:\d{2}:\d{2}$/
    ];

    // Check if the string matches any date pattern
    const matchesPattern = datePatterns.some(pattern => pattern.test(value));

    // Additional validation: Check if it creates a valid date object
    if (matchesPattern) {
        const date = new Date(value);
        return !isNaN(date.getTime());
    }

    return false;
};

// Styles for react-select
const selectStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: '38px',
        borderRadius: '0.25rem',
        borderColor: '#ced4da',
        boxShadow: 'none',
        '&:hover': {
            borderColor: '#80bdff'
        }
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 9999,
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e9ecef' : 'white',
        color: state.isSelected ? 'white' : '#212529',
        cursor: 'pointer',
        ':active': {
            backgroundColor: state.isSelected ? '#0d6efd' : '#e9ecef'
        }
    }),
};

const ReactStrapDataTable = ({
                                 dataSource,
                                 columns,
                                 title,
                                 rowKey = "id",
                                 exportCommon = true,
                                 exportFunction = null,
                                 loading = false,
                                 defaultFilters = {},
                                 onFilterChange = null,
                                 autoDetectDates = true
                             }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [showFilters, setShowFilters] = useState(false);
    const [columnFilters, setColumnFilters] = useState(defaultFilters || {});
    const [tooltipOpen, setTooltipOpen] = useState(null);
    const [detectedColumnTypes, setDetectedColumnTypes] = useState({});

    // Filter options for each column
    const [filterOptions, setFilterOptions] = useState({});

    // Format date for display
    const formatDateDisplay = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    // Detect column types in the data
    const detectColumnTypes = useCallback((data, cols) => {
        if (!data || !data.length) return {};

        const columnTypes = {};

        cols.forEach(column => {
            const { dataIndex, key, filterType } = column;
            const columnKey = dataIndex || key;

            // Skip if filter type is already specified
            if (filterType) return;

            // Sample the first few rows to determine column type
            const sampleSize = Math.min(20, data.length);
            const samples = [];

            for (let i = 0; i < sampleSize; i++) {
                const value = data[i][columnKey];
                if (value !== null && value !== undefined) {
                    samples.push(value);
                }
            }

            if (samples.length === 0) return;

            // Check if majority of samples are date strings
            const dateStringCount = samples.filter(value => isDateString(value)).length;
            if (dateStringCount > samples.length * 0.5) {
                columnTypes[columnKey] = 'date';
            }
        });

        return columnTypes;
    }, []);

    // Effect to generate filter options when dataSource changes
    useEffect(() => {
        if (!dataSource || !dataSource.length) return;

        // Detect date columns if autoDetect is enabled
        const detected = autoDetectDates ? detectColumnTypes(dataSource, columns) : {};
        setDetectedColumnTypes(detected);

        const options = {};

        columns.forEach(column => {
            const { dataIndex, key } = column;
            const columnKey = dataIndex || key;

            // Determine filter type (explicit or detected)
            const filterType = column.filterType || detected[columnKey] || 'select';

            // Skip generating options for date columns
            if (filterType === 'date') return;

            if (filterType === 'select' || filterType === 'multiselect') {
                // Get unique values for this column
                const uniqueValues = new Set();

                dataSource.forEach(item => {
                    const value = item[columnKey];

                    if (value === null || value === undefined) return;

                    if (Array.isArray(value)) {
                        value.forEach(v => {
                            if (v !== null && v !== undefined) {
                                uniqueValues.add(String(v));
                            }
                        });
                    } else {
                        uniqueValues.add(String(value));
                    }
                });

                // Convert to react-select options format
                options[columnKey] = Array.from(uniqueValues)
                    .sort()
                    .map(value => ({ value, label: value }));
            }
        });

        setFilterOptions(options);
    }, [dataSource, columns, autoDetectDates, detectColumnTypes]);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
        if (onFilterChange) {
            onFilterChange(columnFilters);
        }
    }, [columnFilters, onFilterChange]);

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const handleColumnFilter = (columnKey, value) => {
        setColumnFilters(prev => {
            if (value === null || (Array.isArray(value) && value.length === 0)) {
                const newFilters = { ...prev };
                delete newFilters[columnKey];
                return newFilters;
            }
            return { ...prev, [columnKey]: value };
        });
    };

    const clearAllFilters = () => {
        setColumnFilters({});
        setSearchTerm('');
    };

    const clearColumnFilter = (columnKey) => {
        setColumnFilters(prev => {
            const newFilters = { ...prev };
            delete newFilters[columnKey];
            return newFilters;
        });
    };

    const isColumnFiltered = useMemo(() => {
        return Object.keys(columnFilters).length > 0;
    }, [columnFilters]);

    const filteredAndSortedData = useMemo(() => {
        if (!dataSource) return [];

        let processedData = [...dataSource];

        // Apply column-specific filters
        if (isColumnFiltered) {
            processedData = processedData.filter(item => {
                return Object.entries(columnFilters).every(([columnKey, filterValue]) => {
                    const itemValue = item[columnKey];
                    const column = columns.find(col => (col.dataIndex || col.key) === columnKey);
                    const filterType = column?.filterType || detectedColumnTypes[columnKey] || 'select';

                    // Skip if value is null/undefined and we're not explicitly looking for nulls
                    if (itemValue === null || itemValue === undefined) {
                        return false;
                    }

                    // Handle date filters (date range)
                    if (filterType === 'date' && typeof filterValue === 'object') {
                        const { startDate, endDate } = filterValue;
                        let itemDate;

                        // Handle string dates
                        if (typeof itemValue === 'string' && isDateString(itemValue)) {
                            itemDate = new Date(itemValue);
                        } else {
                            itemDate = new Date(itemValue);
                        }

                        // Check if valid date
                        if (isNaN(itemDate.getTime())) return false;

                        if (startDate && endDate) {
                            return itemDate >= new Date(startDate) && itemDate <= new Date(endDate);
                        } else if (startDate) {
                            return itemDate >= new Date(startDate);
                        } else if (endDate) {
                            return itemDate <= new Date(endDate);
                        }

                        return true;
                    }

                    // Handle different filter types
                    if (Array.isArray(filterValue)) {
                        // Multi-select filter (both 'select' and 'multiselect' can now be multi-selected)
                        const filterValues = filterValue.map(f => f.value);

                        if (filterValues.length === 0) return true; // No filter selected

                        if (Array.isArray(itemValue)) {
                            // Item value is an array, check for any match
                            return itemValue.some(v => filterValues.includes(String(v)));
                        }

                        return filterValues.includes(String(itemValue));
                    } else if (typeof filterValue === 'object' && filterValue !== null && !filterValue.startDate && !filterValue.endDate) {
                        // Single-select filter
                        if (Array.isArray(itemValue)) {
                            // Item value is an array, check for any match
                            return itemValue.some(v => String(v) === filterValue.value);
                        }

                        return String(itemValue) === filterValue.value;
                    }

                    // Boolean, number or other direct comparison
                    return String(itemValue) === String(filterValue);
                });
            });
        }

        // Apply global search
        if (searchTerm && searchTerm.trim()) {
            const normalizedSearchTerm = searchTerm.trim().toLowerCase();

            processedData = processedData.filter(item => {
                // Only search through the columns that are specified in the columns prop
                return columns.some(column => {
                    const value = item[column.dataIndex || column.key];

                    if (value === null || value === undefined) {
                        return false;
                    }

                    if (typeof value === 'string') {
                        return value.toLowerCase().includes(normalizedSearchTerm);
                    }
                    if (typeof value === 'number') {
                        return value.toString().includes(normalizedSearchTerm);
                    }
                    if (typeof value === 'boolean') {
                        return value.toString().toLowerCase().includes(normalizedSearchTerm);
                    }
                    if (Array.isArray(value)) {
                        return value.some(v =>
                            String(v).toLowerCase().includes(normalizedSearchTerm)
                        );
                    }
                    return false;
                });
            });
        }

        // Sort implementation
        if (sortConfig.key) {
            processedData.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                // Handle null/undefined values in sorting
                if (aValue === null || aValue === undefined) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (bValue === null || bValue === undefined) return sortConfig.direction === 'ascending' ? 1 : -1;

                // Handle different types of values
                if (typeof aValue === 'string') {
                    return sortConfig.direction === 'ascending'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }

                // Handle numeric and other types
                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }

        return processedData;
    }, [dataSource, searchTerm, sortConfig, columns, columnFilters, isColumnFiltered, detectedColumnTypes]);

    const TooltipCell = ({ content, maxLines = 2 }) => {
        const [isHovered, setIsHovered] = useState(false);
        const contentRef = useRef(null);
        const [isOverflowing, setIsOverflowing] = useState(false);
        const cellId = useRef(`tooltip-cell-${Math.random().toString(36).substr(2, 9)}`).current;

        useEffect(() => {
            if (contentRef.current) {
                const element = contentRef.current;
                setIsOverflowing(element.scrollHeight > element.clientHeight);
            }
        }, [content]);

        return (
            <div
                id={cellId}
                className="position-relative"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div
                    ref={contentRef}
                    style={{
                        maxHeight: !isHovered ? `${maxLines * 1.5}em` : 'none',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                        transition: 'max-height 0.3s ease-in-out',
                        position: 'relative'
                    }}
                >
                    {content}
                    {isOverflowing && !isHovered && (
                        <span className="text-primary">...</span>
                    )}
                </div>

                {isOverflowing && (
                    <UncontrolledTooltip
                        target={cellId}
                        placement="top"
                        fade={true}
                    >
                        {typeof content === 'string' ? content : 'Click to expand'}
                    </UncontrolledTooltip>
                )}
            </div>
        );
    };

    const pageCount = Math.ceil(filteredAndSortedData.length / itemsPerPage);
    const paginatedData = useMemo(() => {
        if (itemsPerPage === -1) return filteredAndSortedData;
        return filteredAndSortedData.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
        );
    }, [filteredAndSortedData, currentPage, itemsPerPage]);

    const paginationInfo = itemsPerPage === -1
        ? `Showing all ${filteredAndSortedData.length} entries`
        : `Showing ${Math.min(filteredAndSortedData.length, 1 + (currentPage - 1) * itemsPerPage)} to ${Math.min(currentPage * itemsPerPage, filteredAndSortedData.length)} of ${filteredAndSortedData.length} entries`;

    const renderPaginationButtons = () => {
        if (filteredAndSortedData.length === 0) return null;

        const totalPages = pageCount;
        if (totalPages === 0) return null;

        const maxButtons = 5; // Show max 5 numbered buttons
        let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
        let endPage = Math.min(totalPages, startPage + maxButtons - 1);

        // Adjust start page if we're near the end
        if (endPage - startPage + 1 < maxButtons) {
            startPage = Math.max(1, endPage - maxButtons + 1);
        }

        const pages = [];

        // Add first page and ellipsis if needed
        if (startPage > 1) {
            pages.push(
                <Button key={1} size="sm" color="secondary" onClick={() => setCurrentPage(1)} className="mx-1">
                    1
                </Button>
            );
            if (startPage > 2) {
                pages.push(<span key="ellipsis1" className="mx-2">...</span>);
            }
        }

        // Add numbered pages
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <Button
                    key={i}
                    size="sm"
                    color={currentPage === i ? 'primary' : 'secondary'}
                    onClick={() => setCurrentPage(i)}
                    className="mx-1"
                >
                    {i}
                </Button>
            );
        }

        // Add last page and ellipsis if needed
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pages.push(<span key="ellipsis2" className="mx-2">...</span>);
            }
            pages.push(
                <Button
                    key={totalPages}
                    size="sm"
                    color="secondary"
                    onClick={() => setCurrentPage(totalPages)}
                    className="mx-1"
                >
                    {totalPages}
                </Button>
            );
        }

        return (
            <div className="d-flex align-items-center flex-wrap">
                <Button
                    size="sm"
                    color="secondary"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="me-2"
                >
                    Previous
                </Button>
                <div className="d-flex flex-wrap">{pages}</div>
                <Button
                    size="sm"
                    color="secondary"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="ms-2"
                >
                    Next
                </Button>
            </div>
        );
    };

    // Render column filters
    const renderColumnFilter = (column) => {
        const { dataIndex, key, title } = column;
        const columnKey = dataIndex || key;
        const options = filterOptions[columnKey] || [];

        // Use explicitly defined filter type or detected type
        const filterType = column.filterType || detectedColumnTypes[columnKey] || 'select';

        if (filterType === 'select') {
            return (
                <div className="mb-2">
                    <small className="text-muted d-block mb-1">{title}</small>
                    <Select
                        options={options}
                        value={columnFilters[columnKey] || null}
                        onChange={(selected) => handleColumnFilter(columnKey, selected)}
                        placeholder={`Filter ${title}`}
                        isClearable
                        isMulti
                        styles={selectStyles}
                        className="column-filter"
                        classNamePrefix="column-filter"
                    />
                </div>
            );
        } else if (filterType === 'multiselect') {
            return (
                <div className="mb-2">
                    <small className="text-muted d-block mb-1">{title}</small>
                    <Select
                        options={options}
                        value={columnFilters[columnKey] || []}
                        onChange={(selected) => handleColumnFilter(columnKey, selected || [])}
                        placeholder={`Filter ${title}`}
                        isClearable
                        isMulti
                        styles={selectStyles}
                        className="column-filter"
                        classNamePrefix="column-filter"
                    />
                </div>
            );
        } else if (filterType === 'boolean') {
            return (
                <div className="mb-2">
                    <small className="text-muted d-block mb-1">{title}</small>
                    <Select
                        options={[
                            { value: 'true', label: 'Yes' },
                            { value: 'false', label: 'No' }
                        ]}
                        value={columnFilters[columnKey] || null}
                        onChange={(selected) => handleColumnFilter(columnKey, selected)}
                        placeholder={`Filter ${title}`}
                        isClearable
                        styles={selectStyles}
                        className="column-filter"
                        classNamePrefix="column-filter"
                    />
                </div>
            );
        } else if (filterType === 'date') {
            return (
                <div className="mb-2">
                    <small className="text-muted d-block mb-1">{title}</small>
                    <div className="d-flex gap-2">
                        <div className="flex-grow-1">
                            <Input
                                type="date"
                                value={columnFilters[columnKey]?.startDate || ''}
                                onChange={(e) => handleColumnFilter(columnKey, {
                                    ...columnFilters[columnKey] || {},
                                    startDate: e.target.value,
                                    display: `${e.target.value ? formatDateDisplay(e.target.value) : ''}${
                                        columnFilters[columnKey]?.endDate ? ' - ' + formatDateDisplay(columnFilters[columnKey].endDate) : ''
                                    }`
                                })}
                                placeholder="From"
                                className="form-control form-control-sm"
                            />
                            <small className="text-muted">From</small>
                        </div>
                        <div className="flex-grow-1">
                            <Input
                                type="date"
                                value={columnFilters[columnKey]?.endDate || ''}
                                onChange={(e) => handleColumnFilter(columnKey, {
                                    ...columnFilters[columnKey] || {},
                                    endDate: e.target.value,
                                    display: `${columnFilters[columnKey]?.startDate ? formatDateDisplay(columnFilters[columnKey].startDate) + ' - ' : ''}${
                                        e.target.value ? formatDateDisplay(e.target.value) : ''
                                    }`
                                })}
                                placeholder="To"
                                className="form-control form-control-sm"
                            />
                            <small className="text-muted">To</small>
                        </div>
                    </div>
                </div>
            );
        } else if (filterType === 'number') {
            return (
                <div className="mb-2">
                    <small className="text-muted d-block mb-1">{title}</small>
                    <Input
                        type="number"
                        value={columnFilters[columnKey] || ''}
                        onChange={(e) => handleColumnFilter(columnKey, e.target.value)}
                        placeholder={`Filter ${title}`}
                    />
                </div>
            );
        }

        return null;
    };

    // Render active filters
    const renderActiveFilters = () => {
        if (!isColumnFiltered) return null;

        return (
            <div className="mb-3">
                <div className="d-flex align-items-center mb-2">
                    <strong className="me-2">Active Filters:</strong>
                    <Button size="sm" color="secondary" onClick={clearAllFilters} className="ms-auto">
                        <ClearOutlined /> Clear All
                    </Button>
                </div>
                <div className="d-flex flex-wrap">
                    {Object.entries(columnFilters).map(([columnKey, filterValue]) => {
                        const column = columns.find(col => (col.dataIndex || col.key) === columnKey);
                        if (!column) return null;

                        let displayValue = '';

                        if (Array.isArray(filterValue)) {
                            // Multi-select
                            displayValue = filterValue.map(v => v.label).join(', ');
                        } else if (typeof filterValue === 'object' && filterValue !== null) {
                            if (filterValue.startDate || filterValue.endDate) {
                                // Date range
                                displayValue = filterValue.display ||
                                    `${filterValue.startDate ? formatDateDisplay(filterValue.startDate) : ''} ${
                                        filterValue.startDate && filterValue.endDate ? ' - ' : ''
                                    } ${filterValue.endDate ? formatDateDisplay(filterValue.endDate) : ''}`;
                            } else {
                                // Single-select
                                displayValue = filterValue.label;
                            }
                        } else {
                            // Simple value
                            displayValue = String(filterValue);
                        }

                        return (
                            <Badge
                                key={columnKey}
                                color="primary"
                                className="me-2 mb-2 p-2 d-flex align-items-center"
                            >
                                <span>{column.title}: {displayValue}</span>
                                <Button
                                    close
                                    size="sm"
                                    className="ms-2"
                                    onClick={() => clearColumnFilter(columnKey)}
                                />
                            </Badge>
                        );
                    })}
                </div>
            </div>
        );
    };

    const formatDataForExport = () => {
        // Get all keys from the first data item
        const firstItem = filteredAndSortedData[0] || {};
        const dataKeys = Object.keys(firstItem);

        // Check if data matches any predefined structure
        let matchedType = null;
        let matchingKeys = {};

        // Check which type has the most matching keys
        Object.entries(uniqueKeys).forEach(([type, typeKeys]) => {
            const matchCount = Object.keys(typeKeys).filter(key =>
                dataKeys.includes(key)).length;

            if (matchCount > 0) {
                // Consider it a match if it has at least one unique key
                matchedType = type;
                matchingKeys = { ...commonKeys, ...typeKeys };
            }
        });

        if (matchedType && exportCommon) {
            return filteredAndSortedData.map(item => {
                const rowData = {};
                Object.entries(matchingKeys).forEach(([key, title]) => {
                    if (key !== 'files') {
                        let value;
                        if (Array.isArray(item[key])) {
                            value = item[key].filter(v => typeof v === 'string').join(', ');
                        } else if (typeof item[key] === 'boolean') {
                            value = item[key] ? 'Yes' : 'No';
                        } else {
                            value = item[key] || '';
                        }
                        rowData[title] = value;
                    }
                });
                return rowData;
            });
        } else {
            // Use columns structure with improved handling
            return filteredAndSortedData.map(item => {
                const rowData = {};
                columns.forEach(col => {
                    if (col.dataIndex !== 'files' && col.key !== 'files') {
                        if (col.render) {
                            // Handle custom renders
                            rowData[col.title] = col.render(item[col.dataIndex], item);
                        } else if (typeof item[col.dataIndex] === 'boolean') {
                            // Handle boolean values
                            rowData[col.title] = item[col.dataIndex] ? 'Yes' : 'No';
                        } else if (Array.isArray(item[col.dataIndex])) {
                            // Handle arrays
                            rowData[col.title] = item[col.dataIndex]
                                .filter(v => typeof v === 'string')
                                .join(', ');
                        } else {
                            // Handle regular values
                            rowData[col.title] = item[col.dataIndex] || item[col.key] || '';
                        }
                    }
                });
                return rowData;
            });
        }
    };

    const exportToPDF = () => {
        // Get all keys from the first data item
        const firstItem = filteredAndSortedData[0] || {};
        const dataKeys = Object.keys(firstItem);

        // Check if data matches any predefined structure
        let matchedType = null;
        let matchingKeys = {};

        // Check which type has the most matching keys
        Object.entries(uniqueKeys).forEach(([type, typeKeys]) => {
            const matchCount = Object.keys(typeKeys).filter(key =>
                dataKeys.includes(key)).length;

            if (matchCount > 0) {
                // Consider it a match if it has at least one unique key
                matchedType = type;
                matchingKeys = { ...commonKeys, ...typeKeys };
            }
        });

        let pdfData;
        let headers;

        if (matchedType && exportCommon) {
            // Use predefined structure
            headers = Object.values(matchingKeys).filter(title => title !== 'Files');
            pdfData = filteredAndSortedData.map(item => {
                const rowData = [];
                Object.entries(matchingKeys).forEach(([key, title]) => {
                    if (key !== 'files') {
                        let value;
                        if (Array.isArray(item[key])) {
                            value = item[key].filter(v => typeof v === 'string').join(', ');
                        } else if (typeof item[key] === 'boolean') {
                            value = item[key] ? 'Yes' : 'No';
                        } else {
                            value = item[key] || '';
                        }
                        rowData.push(value);
                    }
                });
                return rowData;
            });
        } else {
            // Use columns structure with improved handling
            headers = columns
                .filter(col => col.dataIndex !== 'files' && col.key !== 'files')
                .map(col => col.title);

            pdfData = filteredAndSortedData.map(item => {
                return columns
                    .filter(col => col.dataIndex !== 'files' && col.key !== 'files')
                    .map(col => {
                        if (col.render) {
                            // Handle custom renders
                            return col.render(item[col.dataIndex], item);
                        } else if (typeof item[col.dataIndex] === 'boolean') {
                            // Handle boolean values
                            return item[col.dataIndex] ? 'Yes' : 'No';
                        } else if (Array.isArray(item[col.dataIndex])) {
                            // Handle arrays
                            return item[col.dataIndex]
                                .filter(v => typeof v === 'string')
                                .join(', ');
                        } else {
                            // Handle regular values
                            return item[col.dataIndex] || '';
                        }
                    });
            });
        }

        // Create PDF document
        const doc = new jsPDF({
            orientation: 'landscape'
        });

        // Add title if type is matched
        if (matchedType) {
            doc.setFontSize(16);
            doc.text(matchedType.toUpperCase(), 14, 15);
            doc.setFontSize(12); // Reset font size
        }

        // Configure table options
        const tableConfig = {
            head: [headers],
            body: pdfData,
            startY: matchedType ? 25 : 15,
            headStyles: {
                fillColor: [66, 66, 66],
                textColor: 255,
                fontStyle: 'bold'
            },
            theme: 'grid',
            styles: {
                fontSize: 10,
                cellPadding: 3,
                overflow: 'linebreak'
            }
        };

        // Generate table
        doc.autoTable(tableConfig);

        // Generate filename
        const currentDate = new Date().toISOString().split('T')[0];
        const filename = `${matchedType ? matchedType.toUpperCase() + '_' : ''}${currentDate}.pdf`;

        // Save the PDF
        doc.save(filename);
    };

    const exportToExcel = () => {
        // Use custom export function if provided
        if (exportFunction) {
            exportFunction(filteredAndSortedData);
            return;
        }

        // Get all keys from the first data item
        const firstItem = filteredAndSortedData[0] || {};
        const dataKeys = Object.keys(firstItem);

        // Check if data matches any predefined structure
        let matchedType = null;
        let matchingKeys = {};

        // Check which type has the most matching keys
        Object.entries(uniqueKeys).forEach(([type, typeKeys]) => {
            const matchCount = Object.keys(typeKeys).filter(key =>
                dataKeys.includes(key)).length;

            if (matchCount > 0) {
                // Consider it a match if it has at least one unique key
                matchedType = type;
                matchingKeys = { ...commonKeys, ...typeKeys };
            }
        });

        let excelData;
        if (matchedType && exportCommon) {
            // Use predefined structure
            excelData = filteredAndSortedData.map(item => {
                const rowData = {};
                Object.entries(matchingKeys).forEach(([key, title]) => {
                    if (key !== 'files') {
                        const value = Array.isArray(item[key])
                            ? item[key].filter(v => typeof v === 'string').join(', ')
                            : item[key];
                        rowData[title] = value || '';
                    }
                });
                return rowData;
            });
        } else {
            // Use columns structure with improved handling
            excelData = filteredAndSortedData.map(item => {
                const row = {};
                columns.forEach(col => {
                    if (col.dataIndex !== 'files' && col.key !== 'files') {
                        if (col.render) {
                            // Handle custom renders
                            row[col.title] = col.render(item[col.dataIndex], item);
                        } else if (typeof item[col.dataIndex] === 'boolean') {
                            // Handle boolean values
                            row[col.title] = item[col.dataIndex] ? 'Yes' : 'No';
                        } else if (Array.isArray(item[col.dataIndex])) {
                            // Handle arrays
                            row[col.title] = item[col.dataIndex]
                                .filter(v => typeof v === 'string')
                                .join(', ');
                        } else {
                            // Handle regular values
                            row[col.title] = item[col.dataIndex] || '';
                        }
                    }
                });
                return row;
            });
        }

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);

        // Set column widths based on content
        const headers = Object.keys(excelData[0] || {});
        const colWidths = headers.map(header => ({ wch: Math.max(String(header).length, 15) }));
        ws['!cols'] = colWidths;

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

        // Generate filename
        const currentDate = new Date().toISOString().split('T')[0];
        const filename = title 
            ? `${title.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_')}_${currentDate}.xlsx`
            : `${matchedType ? matchedType.toUpperCase() + '_' : ''}${currentDate}.xlsx`;

        // Create Excel file and trigger download
        XLSX.writeFile(wb, filename);
    };

    return (
        <Card className="shadow-sm">
            <CardBody>
                <Row className="mb-3">
                    <Col sm="4">
                        <div className="position-relative">
                            <Input
                                type="text"
                                className="form-control search"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                            {searchTerm && (
                                <Button
                                    close
                                    size="sm"
                                    className="position-absolute"
                                    style={{ right: '10px', top: '8px' }}
                                    onClick={() => setSearchTerm('')}
                                />
                            )}
                        </div>
                    </Col>
                    <Col sm="4">
                        <Input
                            type="select"
                            id="recordsPerPage"
                            name="recordsPerPage"
                            value={itemsPerPage}
                            onChange={(e) => setItemsPerPage(Number(e.target.value))}
                        >
                            <option value="50">50 per page</option>
                            <option value="100">100 per page</option>
                            <option value="150">150 per page</option>
                            <option value="200">200 per page</option>
                            <option value="400">400 per page</option>
                            <option value="500">500 per page</option>
                            <option value="-1">All</option>
                        </Input>
                    </Col>
                    <Col sm="4" className="d-flex justify-content-end">
                        <ButtonGroup>
                            <Button
                                color={showFilters ? 'primary' : 'secondary'}
                                onClick={() => setShowFilters(!showFilters)}
                                className="me-1"
                                id="toggleFilters"
                            >
                                <FilterOutlined /> {isColumnFiltered && <Badge color="light" pill className="ms-1">{Object.keys(columnFilters).length}</Badge>}
                            </Button>
                            <UncontrolledTooltip target="toggleFilters">
                                {showFilters ? 'Hide Filters' : 'Show Filters'}
                            </UncontrolledTooltip>

                            <Button color="primary" onClick={exportToExcel} id="exportExcel">
                                <FileExcelOutlined/> {exportFunction ? 'Export' : 'Export'}
                            </Button>
                            <UncontrolledTooltip target="exportExcel">
                                Export to Excel
                            </UncontrolledTooltip>
                        </ButtonGroup>
                    </Col>
                </Row>

                {/* Column Filters */}
                <Collapse isOpen={showFilters}>
                    <Card className="mb-3 border">
                        <CardBody className="bg-light">
                            <Row>
                                {Object.keys(detectedColumnTypes).length > 0 && (
                                    <div className="mb-2 mt-n2">
                                        <small className="text-muted">
                                            <Badge color="info" pill className="me-1">Auto</Badge>
                                            Date columns detected: {Object.keys(detectedColumnTypes).map(key => {
                                            const col = columns.find(c => (c.dataIndex || c.key) === key);
                                            return col ? col.title : key;
                                        }).join(', ')}
                                        </small>
                                    </div>
                                )}
                                {columns.map(column => (
                                    <Col key={column.key || column.dataIndex} md="3" sm="6" className="mb-2">
                                        {renderColumnFilter(column)}
                                    </Col>
                                ))}
                            </Row>
                        </CardBody>
                    </Card>
                </Collapse>

                {/* Active Filters */}
                {renderActiveFilters()}

                {/* Loading Indicator */}
                {loading && (
                    <div className="text-center py-4">
                        <Spinner color="primary" />
                        <p className="mt-2">Loading data...</p>
                    </div>
                )}

                {/* Table */}
                {!loading && (
                    <AutoSizer disableHeight>
                        {({ width }) => (
                            <div style={{ width, overflowX: 'auto', overflowY: 'auto', maxHeight: '500px' }}>
                                <Table responsive striped hover style={{ width }} className="border">
                                    <thead>
                                    <tr>
                                        {columns.map(column => (
                                            <th
                                                key={column.key}
                                                onClick={() => requestSort(column.dataIndex || column.key)}
                                                style={{
                                                    cursor: 'pointer',
                                                    position: 'sticky',
                                                    top: 0,
                                                    backgroundColor: '#f8f9fa',
                                                    zIndex: 1,
                                                    boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)'
                                                }}
                                                className={columnFilters[column.dataIndex || column.key] ? 'bg-light' : ''}
                                            >
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <span>{column.title}</span>
                                                    <span>
                                                            {sortConfig.key === (column.dataIndex || column.key) && (
                                                                <span className="ms-1">
                                                                    {sortConfig.direction === 'ascending' ? '▲' : '▼'}
                                                                </span>
                                                            )}
                                                        {columnFilters[column.dataIndex || column.key] && (
                                                            <FilterOutlined className="ms-1 text-primary" />
                                                        )}
                                                        </span>
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {paginatedData.map((item) => (
                                        <tr key={item[rowKey]}>
                                            {columns.map(column => (
                                                <td key={column.key}>
                                                    {column.showTooltip ? (
                                                        <TooltipCell
                                                            content={column.render
                                                                ? column.render(item[column.dataIndex || column.key], item)
                                                                : item[column.dataIndex || column.key]
                                                            }
                                                            maxLines={column.maxLines || 2}
                                                        />
                                                    ) : (
                                                        column.render
                                                            ? column.render(item[column.dataIndex || column.key], item)
                                                            : item[column.dataIndex || column.key]
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </AutoSizer>
                )}

                {!loading && paginatedData.length === 0 && (
                    <div className="text-center my-5 py-5">
                        <SettingOutlined style={{ fontSize: '2rem' }} className="text-muted mb-3" />
                        <h5>No Results Found</h5>
                        <p className="text-muted mb-3">
                            {isColumnFiltered || searchTerm
                                ? "Try adjusting your search or filter criteria."
                                : "There are no records to display."}
                        </p>
                        {(isColumnFiltered || searchTerm) && (
                            <Button color="secondary" onClick={clearAllFilters}>
                                Clear All Filters
                            </Button>
                        )}
                    </div>
                )}

                <div className="d-flex flex-wrap justify-content-between align-items-center mt-3">
                    <div className="text-muted mb-2">
                        {paginationInfo}
                    </div>
                    <div className="mb-2">
                        {renderPaginationButtons()}
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

export default ReactStrapDataTable;